/**
 * Unified Kanban primitives.
 *
 * A single visual language for every drag-free Kanban in the app — Invoice
 * Board, Orders Board, Support Board, Account Management Board — matching
 * the installer-facing Lead CRM.
 *
 *   <KanbanColumn column={{ key, label, colour, icon }} count={rows.length}>
 *     { rows.map(r => <MyCard row={r} moveTo={<MoveToDropdown …/>} />) }
 *   </KanbanColumn>
 *
 * `<MoveToDropdown>` handles the "MOVE TO" native select — same behaviour
 * as the Lead CRM cards. No HTML5 drag-drop anywhere.
 */
import React from 'react';

/**
 * Standard palette used across every board so the same stage kind carries
 * the same colour regardless of which module you're looking at.
 * Values match the ManageLeads CRM_STATUSES palette.
 */
export const KANBAN_PALETTE = {
  emerald: '#10B981',  // fresh / new / paid
  blue:    '#3B82F6',  // in progress / awaiting-customer
  violet:  '#8B5CF6',  // scheduled / processed
  amber:   '#F59E0B',  // priority / awaiting-us / quoted
  green:   '#22C55E',  // complete / delivered / won
  red:     '#EF4444',  // lost / rejected
  slate:   '#64748B',  // neutral / new-enquiry
  indigo:  '#6366F1',  // active-customer / final
  sky:     '#0EA5E9',  // in transit
  rose:    '#F43F5E',  // urgent
};

export function KanbanColumn({ column, count, children, testId, emptyLabel = 'No records' }) {
  const Icon = column.icon;
  return (
    <div className="w-72 flex-shrink-0" data-testid={testId || `kanban-column-${column.key}`}>
      <div
        className="rounded-t-lg px-4 py-3 flex items-center justify-between"
        style={{ backgroundColor: column.colour }}
      >
        <div className="flex items-center gap-2 text-white min-w-0">
          {Icon ? <Icon className="w-4 h-4 shrink-0" /> : null}
          <span className="font-semibold text-sm truncate">{column.label}</span>
        </div>
        <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full shrink-0">
          {count}
        </span>
      </div>
      <div className="bg-gray-100 rounded-b-lg p-3 min-h-[400px] space-y-3">
        {count === 0 && (
          <div className="text-center py-8 text-gray-400 text-sm">{emptyLabel}</div>
        )}
        {children}
      </div>
    </div>
  );
}

/**
 * Native <select> "MOVE TO" widget that lives on each Kanban card. Reuses
 * the exact styling from Lead CRM so every board feels identical.
 *
 * Consumers wire `options` as `[{ key, label }]` and `onChange(target)`
 * fires when the user picks a new stage (guarded — no-op when same).
 */
export function MoveToDropdown({ current, options, onChange, disabled, testId }) {
  return (
    <div onClick={(e) => e.stopPropagation()} className="space-y-1">
      <div className="text-[10px] font-bold uppercase text-gray-500 tracking-wide">Move to</div>
      <select
        value={current}
        disabled={disabled}
        onChange={(e) => {
          const target = e.target.value;
          if (target && target !== current) onChange?.(target);
        }}
        data-testid={testId}
        aria-label="Move to stage"
        className="w-full text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white hover:border-gray-400 focus:outline-none focus:border-slate-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {options.map((o) => (
          <option key={o.key} value={o.key}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

export default KanbanColumn;
